// ── BCHNostr — Verified BCH Business badge ────────────────────────
//
// Renders the ✅ Verified BCH Business badge for a business whose
// issuer-signed grant is currently active. Clicking it reveals the details:
// who verified, when, until when, and the business Nostr identity.
//
// Honesty guardrails (per spec):
//   · Never says "Currently accepting BCH".
//   · Never implies government/legal verification.
//   · States plainly what verification means: the business completed
//     BCHNostr's verification process.

import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { BadgeCheck, X, ShieldCheck, Calendar, Key } from 'lucide-react';
import { nip19 } from 'nostr-tools';
import { formatVerifyDate, type VerificationState } from '../lib/businessVerify';

interface VerifiedBusinessBadgeProps {
  businessPubkey: string;
  state: VerificationState;
  /** Compact rendering for directory cards / strips. */
  size?: 'sm' | 'md';
  /** Disable the details popover (badge becomes a static label). */
  static?: boolean;
}

export default function VerifiedBusinessBadge({
  businessPubkey, state, size = 'md', static: isStatic = false,
}: VerifiedBusinessBadgeProps) {
  const [open, setOpen] = useState(false);

  if (state.status !== 'VERIFIED') return null;

  const isSm = size === 'sm';
  const label = (
    <span
      className={`inline-flex items-center gap-1 rounded-full bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 font-semibold ${
        isSm ? 'px-1.5 py-0.5 text-[10px]' : 'px-2 py-0.5 text-xs'
      }`}
    >
      <BadgeCheck className={isSm ? 'w-3 h-3' : 'w-3.5 h-3.5'} />
      Verified BCH Business
    </span>
  );

  if (isStatic) return label;

  return (
    <>
      <button
        type="button"
        onClick={e => { e.preventDefault(); e.stopPropagation(); setOpen(true); }}
        className="no-navigate inline-flex hover:opacity-80 transition-opacity"
        title="Verified by BCHNostr — tap for details"
      >
        {label}
      </button>
      {open && (
        <VerificationDetails
          businessPubkey={businessPubkey}
          state={state}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}

function VerificationDetails({
  businessPubkey, state, onClose,
}: {
  businessPubkey: string;
  state: VerificationState;
  onClose: () => void;
}) {
  const npub = (() => {
    try { return nip19.npubEncode(businessPubkey); } catch { return businessPubkey; }
  })();

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/60 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl bg-popover border border-border shadow-2xl overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 pt-5 pb-3">
          <h2 className="font-bold text-base flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-emerald-500/15 flex items-center justify-center">
              <ShieldCheck className="w-4 h-4 text-emerald-500" />
            </div>
            Verified by BCHNostr
          </h2>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-muted">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="px-5 pb-5 space-y-4">
          <div className="flex items-center gap-2 rounded-xl bg-emerald-500/10 border border-emerald-500/20 px-3 py-2.5">
            <BadgeCheck className="w-5 h-5 text-emerald-500 shrink-0" />
            <p className="text-sm font-semibold text-emerald-600 dark:text-emerald-400">
              Verified BCH Business
            </p>
          </div>

          <dl className="space-y-2.5 text-xs">
            <div className="flex items-center gap-2">
              <Calendar className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
              <dt className="text-muted-foreground w-28 shrink-0">Verified on</dt>
              <dd className="font-medium">{formatVerifyDate(state.startAt)}</dd>
            </div>
            <div className="flex items-center gap-2">
              <Calendar className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
              <dt className="text-muted-foreground w-28 shrink-0">Expires</dt>
              <dd className="font-medium">{formatVerifyDate(state.expiresAt)}</dd>
            </div>
            <div className="flex items-start gap-2">
              <Key className="w-3.5 h-3.5 text-muted-foreground shrink-0 mt-0.5" />
              <dt className="text-muted-foreground w-28 shrink-0">Business identity</dt>
              <dd className="font-mono text-[10px] break-all min-w-0">{npub}</dd>
            </div>
          </dl>

          <p className="text-[10px] text-muted-foreground leading-relaxed border-t border-border pt-3">
            This business completed BCHNostr's business verification process. This is
            <strong className="text-foreground"> not</strong> a government or legal verification,
            and it does <strong className="text-foreground">not</strong> guarantee the business is
            currently accepting BCH.
          </p>
        </div>
      </div>
    </div>,
    document.body,
  );
}
